import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Wrench, Users, Shield, Clock, Phone, Headphones, Tool, Activity, AlertCircle, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEOHead from '../components/seo/SEOHead';
import Breadcrumbs from '../components/seo/Breadcrumbs';

const AfterSalesService = () => {
  const services = [
    {
      id: 'service-install',
      icon: Wrench,
      title: 'Installation & Commissioning',
      desc: 'Complete on-site installation by experienced technicians with thorough testing and quality validation before handover.',
      features: ['Site preparation guidance', 'Professional installation', 'Performance testing', 'Final commissioning']
    },
    {
      id: 'service-training',
      icon: Users,
      title: 'Operator Training',
      desc: 'Comprehensive hands-on training for your operators covering machine operation, maintenance, troubleshooting, and safety protocols.',
      features: ['Operation procedures', 'Maintenance training', 'Safety protocols', 'Troubleshooting basics']
    },
    {
      id: 'service-preventive',
      icon: Activity,
      title: 'Preventive Maintenance',
      desc: 'Scheduled preventive maintenance visits to ensure optimal machine performance and prevent unexpected breakdowns.',
      features: ['Regular inspections', 'Lubrication services', 'Component checks', 'Performance optimization']
    },
    {
      id: 'service-spare',
      icon: Wrench,
      title: 'Spare Parts Availability',
      desc: 'Ready stock of genuine spare parts at our Jaipur facility for quick delivery across India within 24-48 hours.',
      features: ['Genuine parts only', 'Quick delivery', 'Competitive pricing', 'Parts warranty']
    },
    {
      id: 'service-technical',
      icon: Headphones,
      title: 'Technical Support',
      desc: '24/7 technical support via phone, WhatsApp, and email for troubleshooting, guidance, and technical queries.',
      features: ['Phone support', 'WhatsApp assistance', 'Email support', 'Video call guidance']
    },
    {
      id: 'service-remote',
      icon: Phone,
      title: 'Remote Assistance',
      desc: 'Real-time remote diagnostics and troubleshooting via video call to resolve issues quickly without site visits.',
      features: ['Video diagnostics', 'Real-time guidance', 'Quick resolution', 'Zero downtime']
    },
    {
      id: 'service-onsite',
      icon: Wrench,
      title: 'On-Site Service',
      desc: 'Prompt on-site service by trained engineers for breakdown repairs, upgrades, and complex troubleshooting.',
      features: ['Trained engineers', 'Fast response', 'Quality repairs', 'Service reports']
    },
    {
      id: 'service-health',
      icon: Shield,
      title: 'Annual Health Check',
      desc: 'Comprehensive annual health check-up of your complete plant including electrical, mechanical, and automation systems.',
      features: ['Complete inspection', 'Performance report', 'Recommendations', 'Preventive measures']
    },
    {
      id: 'service-emergency',
      icon: AlertCircle,
      title: 'Emergency Breakdown Support',
      desc: 'Priority emergency support for critical breakdowns with engineer deployment within 24 hours across major cities.',
      features: ['Priority response', 'Quick deployment', 'Critical repairs', 'Backup solutions']
    }
  ];

  return (
    <div className="min-h-screen pb-20 lg:pb-0">
      <SEOHead
        title="After-Sales Service & Support | Alden Technologies"
        description="24x7 technical support, genuine spare parts, preventive maintenance and operator training for your mineral water plant machinery from Alden Technologies."
        path="/after-sales-service"
      />
      <Breadcrumbs items={[{ label: 'After-Sales Service' }]} />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-alden-green-dark via-alden-green to-alden-green-light text-white py-20 md:py-28">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-block bg-white/20 px-6 py-3 rounded-full text-sm font-semibold mb-6">
            🛠️ Comprehensive Support
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            After Sales <span className="text-yellow-300">Service & Support</span>
          </h1>
          <p className="text-lg sm:text-xl max-w-3xl mx-auto text-white/90 leading-relaxed">
            Complete after-sales support including installation, training, maintenance, spare parts, and 24/7 technical assistance across India.
          </p>
        </div>
      </section>

      {/* Service Highlights */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our <span className="text-alden-green">Service Offerings</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Complete lifecycle support from installation to maintenance ensuring smooth operations and maximum uptime.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service) => (
              <Card key={service.id} className="border-2 border-alden-green/20 hover:border-alden-green hover:shadow-xl transition-all">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-alden-green/10 rounded-full flex items-center justify-center mb-6">
                    <service.icon className="w-10 h-10 text-alden-green" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{service.desc}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature) => (
                      <li key={`${service.id}-${feature}`} className="flex items-center gap-2 text-gray-700 text-sm">
                        <CheckCircle className="w-4 h-4 text-alden-green flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Support Stats */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            {[
              { id: 'support-24-7', value: '24/7', label: 'Technical Support', icon: Clock },
              { id: 'support-plants', value: '100+', label: 'Plants Serviced', icon: Shield },
              { id: 'support-delivery', value: '24-48 hrs', label: 'Spare Parts Delivery', icon: Wrench },
              { id: 'support-coverage', value: '15+ States', label: 'Service Coverage', icon: Phone }
            ].map((stat) => (
              <Card key={stat.id} className="border-2 border-alden-green/20">
                <CardContent className="p-8">
                  <stat.icon className="w-12 h-12 text-alden-green mx-auto mb-4" />
                  <div className="text-4xl font-bold text-alden-green mb-2">{stat.value}</div>
                  <div className="text-gray-600 font-semibold">{stat.label}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-alden-green to-alden-green-light text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Need Technical Support?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-white/90">
            Our service team is ready to assist you with installation, maintenance, spare parts, or emergency support.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="tel:+919351292512">
              <Button className="bg-white text-alden-green hover:bg-gray-100 py-6 px-10 text-lg font-semibold">
                <Phone className="mr-2" />
                Call Service Team
              </Button>
            </a>
            <a href="https://wa.me/919351292512?text=I need technical support" target="_blank" rel="noopener noreferrer">
              <Button className="bg-green-500 hover:bg-green-600 text-white py-6 px-10 text-lg font-semibold">
                WhatsApp Support
              </Button>
            </a>
            <Link to="/contact">
              <Button className="bg-alden-navy hover:bg-alden-navy-light text-white py-6 px-10 text-lg font-semibold">
                <Mail className="mr-2" />
                Email Support
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AfterSalesService;